import { Injectable, isDevMode } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Howl } from 'howler';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { TableDataModel } from 'src/app/shared/models/CommandCenterUI/TableDataModel.model';
import { LogLevel } from 'src/app/shared/models/enums/log-level.enum';
import { ProductUseFunction } from 'src/app/shared/models/enums/ProductUseFunction.enum';
import { Store } from 'src/app/shared/models/store';
import { References } from 'src/app/shared/references';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../environments/environment';
import { FocuslinkService } from './focuslink.service';

@Injectable({
	providedIn: 'root',
})
export class AppService {
	debug: boolean = false;
	env = environment;
	modal: NgbModal = this.modalService;
	moment: any = moment;
	momentDurationFormat = momentDurationFormatSetup;
	msgSvc: MessageService = this.msgService;

	tableColumnOverlayPanelDisplay: Subject<any> = new Subject<any>();
	tableColumnOverlayPanelContent: TableDataModel = new TableDataModel();

	// loading: boolean = false;
	useKey: string;
	venueKey: number;
	venue: Store;
	references = References;

	constructor(
		private modalService: NgbModal,
		private msgService: MessageService,
		private focuslink: FocuslinkService,
		public spinner: NgxSpinnerService,
		public confirmation: ConfirmationService,
		public deviceService: DeviceDetectorService,
		public titleService: Title,
		public router: Router
	) {}

	clone<T>(obj: T): T {
		return JSON.parse(JSON.stringify(obj));
	}

	copyMessage(val: string) {
		const selBox = document.createElement('textarea');
		selBox.style.position = 'fixed';
		selBox.style.left = '0';
		selBox.style.top = '0';
		selBox.style.opacity = '0';
		selBox.value = val;
		document.body.appendChild(selBox);
		selBox.focus();
		selBox.select();
		document.execCommand('copy');
		document.body.removeChild(selBox);
	}

	delay(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	devLog(msg: any) {
		if (isDevMode() || this.debug) {
			console.log(msg);
		}
	}

	async initProduct(venueKey: number, product: string, productFunction: string, checkKey?: string): Promise<string> {
		switch (productFunction) {
			case ProductUseFunction.CARTITEM: {
				const body = {
					cartItemAdded: true,
				};
				return await this.focuslink.productUsePatch(venueKey, this.useKey, body);
			}
			case ProductUseFunction.STARTED: {
				const res = await this.focuslink.productUsePost(venueKey, product, this.deviceService);
				this.useKey = res.useKey;
				return res.useKey;
			}
			case ProductUseFunction.TRANSACTIONCOMPLETE: {
				const body = {
					transactionComplete: true,
					checkKey: checkKey,
				};
				return await this.focuslink.productUsePatch(venueKey, this.useKey, body);
			}
		}
	}

	keys(obj: any) {
		if (!obj || obj.length === 0) {
			return;
		}
		let keyArray: string[] = [];
		if (Array.isArray(obj)) {
			obj.forEach((arrayProperty) => {
				for (let key of Object.keys(arrayProperty)) {
					keyArray.push(key);
					if (typeof arrayProperty[key] === 'object' && !Array.isArray(arrayProperty[key]) && arrayProperty[key]) {
						for (let subKey of Object.keys(arrayProperty[key])) {
							keyArray.push(`${key}.${subKey}`);
						}
					}
				}
			});
			keyArray = Array.from(new Set(keyArray));
		}

		return keyArray;
	}

	loading(show: boolean) {
		switch (show) {
			case true:
				this.spinner.show();
				setTimeout(() => {
					this.spinner.hide();
				}, 5000);
				break;
			case false:
				this.spinner.hide();
				break;
			default:
				this.spinner.hide();
				break;
		}
	}

	log(level: string, message: string, venueKey?: number, meta?: any) {
		this.focuslink.log(venueKey, message, level || LogLevel.ERROR, meta);
	}

	async generatePdfForDownload(targetElementId: string, fileName: string) {
		this.loading(true);
		var data = document.getElementById(targetElementId);
		const defaultShadowStyle = window.getComputedStyle(data).boxShadow;
		setShadowStyle(data, 'none');
		const canvas = await html2canvas(data, {
			scale: 2,
		});

		const imgData = canvas.toDataURL('image/png');
		var imgWidth = 208;
		var pageHeight = 298;
		var imgHeight = (canvas.height * imgWidth) / canvas.width;
		var heightLeft = imgHeight;

		var doc = new jsPDF('p', 'mm', 'a4', true);
		var position = 0;
		doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
		heightLeft -= pageHeight;

		while (heightLeft >= 0) {
			position = heightLeft - imgHeight;
			doc.addPage();
			doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
			heightLeft -= pageHeight;
		}

		doc.save(`${fileName}.pdf`);
		this.loading(false);

		// const binary = doc.output();
		// setShadowStyle(data, defaultShadowStyle);

		// return binary ? btoa(binary) : "";

		// A function that assigns a shadow style to an element
		function setShadowStyle(el, shadowStyle) {
			el.style.boxShadow = shadowStyle;
		}
	}

	playAudio(userTone?: number, source?: string) {
		let audioFile: string;
		switch (userTone) {
			case 0: {
				return;
			}
			case 1: {
				audioFile = 'cashregister.wav';
				break;
			}
			case 2: {
				audioFile = 'wereinthemoney.mp3';
				break;
			}
			case 3: {
				audioFile = 'redalert.mp3';
				break;
			}
			default: {
				return;
			}
		}

		if (audioFile) {
			var sound = new Howl({
				src: [`../assets/sounds/${audioFile}`],
			});
			sound.play();
		}

		return;
	}

	routeExternal(url: string) {
		window.location.href = url;
	}

	uuidv4(): string {
		return uuidv4();
	}
}
